import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eraser, Scissors, Layers, ArrowLeft, ArrowRight, FileText, Loader2 } from 'lucide-react';
import { useDocumentContext } from '../context/DocumentContext';
import { getApiBase } from '../config';

const API_BASE_URL = getApiBase();

export default function RedactionWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingPnote, setLoadingPnote] = useState(false);

  // Retrieve the data passed from the SmartImportPage
  const { file, analysisData: analysis, setProcessMode } = useDocumentContext();

  // --- ERROR STATE: NO FILE ---
  if (!file || !analysis) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col items-center justify-center p-10 text-center font-sans">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Context Found</h2>
        <p className="text-gray-500 dark:text-slate-400 mb-6">Please upload a file first.</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
        >
          Go to Upload
        </button>
      </div>
    );
  }

  // --- LOGIC: Check Document Count ---
  const documentsFound = analysis.document_analysis || [];
  const docCount = documentsFound.length;
  // If no specific analysis, assume we can split manually if pages > 1
  const canSplit = docCount > 0 || (analysis.meta?.total_pages || 0) > 1;

  // --- NAVIGATION HANDLERS ---
  const handleRedactOnly = () => { setProcessMode('redact'); navigate('/editor', { state: { mode: 'redact-only' } }); };
  const handleSplitOnly = () => { setProcessMode('split'); navigate('/splitter'); };
  const handleRedactAndSplit = () => { setProcessMode('both'); navigate('/editor', { state: { mode: 'combo' } }); };
  const handlePromissoryNote = async () => {
    if (!file) return;
    setLoadingPnote(true);
    try {
      // Fetch SSN-based segments from ADS manifest
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE_URL}/analyze/ssn-segments`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`SSN parse failed (${res.status})`);
      const data = await res.json();

      // Replace document_analysis in context with SSN segments
      if (analysis && data.document_analysis) {
        analysis.document_analysis = data.document_analysis;
      }

      setProcessMode('promissory_note');
      // Follow same combo flow: Editor (PII scan) → Splitter (SSN segments already in context)
      navigate('/editor', { state: { mode: 'combo' } });
    } catch (err: any) {
      alert(`Failed to parse SSN segments: ${err.message}`);
    } finally {
      setLoadingPnote(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4 font-sans animate-in fade-in duration-500 transition-colors">
      <div className="max-w-5xl w-full">
        
        {/* Header Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Back to Analysis
        </button>

        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Choose Your Workflow</h1>
          <p className="text-gray-600 dark:text-slate-400 max-w-lg mx-auto">
            Processing <strong>{file.name}</strong> ({docCount} document{docCount !== 1 ? 's' : ''} detected) under 
            <span className="text-blue-600 dark:text-blue-400 font-semibold"> {analysis.jurisdiction_and_context?.jurisdiction_state || "Unknown"}</span> protocols.
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

          {/* --- OPTION 1: REDACT ONLY --- */}
          <div 
            onClick={handleRedactOnly}
            className={`
              group relative flex flex-col h-full p-8 rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden shadow-sm hover:shadow-xl
              bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50/30
              dark:bg-slate-800 dark:border-slate-700 dark:hover:border-blue-500 dark:hover:bg-slate-700
            `}
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Eraser className="w-24 h-24 text-gray-900 dark:text-white" />
            </div>
            
            <div className="bg-blue-100 dark:bg-blue-500/20 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Eraser className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Redact Only</h3>
            
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 flex-1">
              Clean the document of sensitive PII (SSN, Accounts) without changing the page structure.
            </p>
            
            <span className="text-blue-600 dark:text-blue-400 text-sm font-bold flex items-center group-hover:translate-x-1 transition-transform mt-auto">
              Launch Editor <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </div>

          {/* --- OPTION 2: SPLIT ONLY --- */}
          <div 
            onClick={canSplit ? handleSplitOnly : undefined}
            className={`
              group relative flex flex-col h-full p-8 rounded-2xl border transition-all duration-300 overflow-hidden shadow-sm
              ${canSplit 
                ? 'cursor-pointer hover:shadow-xl bg-white border-gray-200 hover:border-purple-500 hover:bg-purple-50/30 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-purple-500 dark:hover:bg-slate-700' 
                : 'cursor-not-allowed opacity-60 bg-gray-100 border-gray-200 dark:bg-slate-800/50 dark:border-slate-800 grayscale-[0.5]'
              }
            `}
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Scissors className="w-24 h-24 text-gray-900 dark:text-white" />
            </div>
            
            <div className="bg-purple-100 dark:bg-purple-500/20 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Scissors className="w-7 h-7 text-purple-600 dark:text-purple-400" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Split Only</h3>
            
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 flex-1">
               Separate the packet into individual named documents (Affidavit, Contract).
            </p>
            
            <span className="text-purple-600 dark:text-purple-400 text-sm font-bold flex items-center group-hover:translate-x-1 transition-transform mt-auto">
                Open Splitter <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </div>

          {/* --- OPTION 3: BOTH --- */}
          <div
            onClick={canSplit ? handleRedactAndSplit : undefined}
            className={`
              group relative flex flex-col h-full p-8 rounded-2xl border transition-all duration-300 overflow-hidden shadow-sm
              ${canSplit
                ? 'cursor-pointer hover:shadow-xl bg-white border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/30 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-500 dark:hover:bg-slate-700'
                : 'cursor-not-allowed opacity-60 bg-gray-100 border-gray-200 dark:bg-slate-800/50 dark:border-slate-800 grayscale-[0.5]'
              }
            `}
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Layers className="w-24 h-24 text-gray-900 dark:text-white" />
            </div>

            <div className="bg-emerald-100 dark:bg-emerald-500/20 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
               <Layers className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Redact & Split</h3>

            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 flex-1">
               Full workflow: Split the packet into components and redact each one individually.
            </p>

            <span className="text-emerald-600 dark:text-emerald-400 text-sm font-bold flex items-center group-hover:translate-x-1 transition-transform mt-auto">
                Start Workflow <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </div>

          {/* --- OPTION 4: PROMISSORY NOTE --- */}
          <div
            onClick={canSplit && !loadingPnote ? handlePromissoryNote : undefined}
            className={`
              group relative flex flex-col h-full p-8 rounded-2xl border transition-all duration-300 overflow-hidden shadow-sm
              ${canSplit && !loadingPnote
                ? 'cursor-pointer hover:shadow-xl bg-white border-gray-200 hover:border-amber-500 hover:bg-amber-50/30 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-amber-500 dark:hover:bg-slate-700'
                : 'cursor-not-allowed opacity-60 bg-gray-100 border-gray-200 dark:bg-slate-800/50 dark:border-slate-800 grayscale-[0.5]'
              }
            `}
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <FileText className="w-24 h-24 text-gray-900 dark:text-white" />
            </div>

            <div className="bg-amber-100 dark:bg-amber-500/20 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
               {loadingPnote
                 ? <Loader2 className="w-7 h-7 text-amber-600 dark:text-amber-400 animate-spin" />
                 : <FileText className="w-7 h-7 text-amber-600 dark:text-amber-400" />
               }
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Promissory Note</h3>

            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 flex-1">
               Split by SSN, redact PII, and extract debtor metadata to CSV.
            </p>

            <span className="text-amber-600 dark:text-amber-400 text-sm font-bold flex items-center group-hover:translate-x-1 transition-transform mt-auto">
                {loadingPnote ? 'Parsing SSNs...' : 'Start Workflow'} {!loadingPnote && <ArrowRight className="w-4 h-4 ml-1" />}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
}